import React from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import type { PracticalMaterial } from '../PracticalMaterials';

interface PracticalMaterialDetailsModalProps {
  visible: boolean;
  material: PracticalMaterial;
  canManage: boolean;
  onHide: () => void;
  onEdit?: () => void;
}

// ── Design tokens (matches Notice modals / PracticalMaterialsBase) ─────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const labelStyle: React.CSSProperties = {
  margin: '0 0 6px 0', fontSize: '0.68rem', color: '#9ca3af',
  fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Owner-level badge — navy/gold palette (mirrors Notice's broadcast-type badge)
const getOwnerBadge = (ownerLevel: string): { bg: string; color: string } => {
  const map: Record<string, { bg: string; color: string }> = {
    institute: { bg: 'rgba(251,178,31,0.18)', color: '#92580a' },
    branch:    { bg: C.gold,                  color: C.navy },
  };
  return map[ownerLevel] || { bg: C.bg, color: C.sub };
};

const getFileIcon  = (mime: string) => mime.includes('pdf') ? 'pi pi-file-pdf' : mime.includes('word') || mime.includes('document') ? 'pi pi-file-word' : mime.includes('excel') || mime.includes('sheet') ? 'pi pi-file-excel' : mime.includes('image') || mime.includes('jpeg') || mime.includes('jpg') ? 'pi pi-image' : 'pi pi-file';
const getFileColor = (mime: string) => mime.includes('pdf') ? '#d32f2f' : mime.includes('word') || mime.includes('document') ? '#1976d2' : mime.includes('excel') || mime.includes('sheet') ? '#388e3c' : mime.includes('image') || mime.includes('jpeg') || mime.includes('jpg') ? '#f57c00' : C.navy;
const formatFileSize = (b: number) => { if (!b) return '0 B'; const k = 1024, s = ['B', 'KB', 'MB']; const i = Math.floor(Math.log(b) / Math.log(k)); return Math.round((b / Math.pow(k, i)) * 100) / 100 + ' ' + s[i]; };

const formatDateTime = (d: string) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const PracticalMaterialDetailsModal: React.FC<PracticalMaterialDetailsModalProps> = ({
  visible,
  material,
  canManage,
  onHide,
  onEdit,
}) => {
  const isInstitute = material.owner_level === 'institute';
  const ownerBadge  = getOwnerBadge(material.owner_level);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '24px' } },
      }}
    >
      <div>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '16px', padding: '28px', marginBottom: '20px',
          color: C.white, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '180px', height: '180px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '100px', height: '100px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-book" style={{ fontSize: '0.7rem' }} />
                  Practical Material
                </div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '1.45rem', fontWeight: '700', color: C.white, lineHeight: '1.3', letterSpacing: '-0.3px' }}>
                  {material.title}
                </h2>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem' }}>
                  <i className="pi pi-calendar" style={{ marginRight: '6px' }} />
                  {formatDateTime(material.created_at)}
                  <span style={{ marginLeft: '14px' }}>
                    <i className="pi pi-user" style={{ marginRight: '5px' }} />
                    Created by {material.created_by_role.replace('_', ' ')}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onHide}
            style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: C.white, width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-align-left" style={{ color: C.gold, fontSize: '0.88rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>Description</h3>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={labelStyle}>Owner Level:</span>
              <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', textTransform: 'capitalize', background: ownerBadge.bg, color: ownerBadge.color, border: isInstitute ? `1px solid ${C.goldBorder}` : 'none' }}>
                {material.owner_level}
              </span>
              {material.scope_type === 'course' && (
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', background: 'rgba(14,34,93,0.08)', color: C.navy }}>
                  General
                </span>
              )}
            </div>
          </div>

          <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: C.body, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
            {material.body}
          </div>

          {!isInstitute && (
            <div style={{ marginTop: '16px', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '10px', padding: '10px 14px', fontSize: '0.8rem', color: '#075985' }}>
              <i className="pi pi-sitemap" style={{ marginRight: '6px' }} />
              Branch-level material — visible only to this branch's students and staff.
            </div>
          )}
          {isInstitute && (
            <div style={{ marginTop: '16px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 14px', fontSize: '0.8rem', color: '#92400e' }}>
              <i className="pi pi-globe" style={{ marginRight: '6px' }} />
              Institute-level material created by super admin — cannot be edited or deleted by admin/operator.
            </div>
          )}
        </div>

        {/* ── Attachments ─────────────────────────────────────────────── */}
        {material.files.length > 0 && (
          <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-paperclip" style={{ color: C.gold, fontSize: '0.88rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>
                Attachments
                <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '20px', background: C.goldDim, color: '#92580a', fontSize: '0.72rem', fontWeight: '700', border: `1px solid ${C.goldBorder}` }}>
                  {material.files.length}
                </span>
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {material.files.map((file, index) => (
                <div
                  key={index}
                  style={{ padding: '14px 16px', background: C.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${C.border}`, transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = C.goldBorder; (e.currentTarget as HTMLDivElement).style.background = 'rgba(251,178,31,0.04)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = C.border; (e.currentTarget as HTMLDivElement).style.background = C.bg; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                    <div style={{ width: '40px', height: '40px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={getFileIcon(file.mime_type)} style={{ color: getFileColor(file.mime_type), fontSize: '1.3rem' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {file.file_name}
                      </p>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>
                        <span>{formatFileSize(file.size)}</span>
                        <span style={{ opacity: 0.5 }}>•</span>
                        <span>{file.mime_type.split('/')[1]?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(file.url, '_blank')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '8px', color: C.white, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', flexShrink: 0, boxShadow: '0 2px 6px rgba(14,34,93,0.2)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.gold; (e.currentTarget as HTMLButtonElement).style.color = C.navy; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,178,31,0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`; (e.currentTarget as HTMLButtonElement).style.color = C.white; e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.2)'; }}
                  >
                    <i className="pi pi-download" style={{ fontSize: '0.75rem' }} />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Video Links ─────────────────────────────────────────────── */}
        {material.video_links.length > 0 && (
          <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-link" style={{ color: C.gold, fontSize: '0.88rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>
                Video Links
                <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '20px', background: C.goldDim, color: '#92580a', fontSize: '0.72rem', fontWeight: '700', border: `1px solid ${C.goldBorder}` }}>
                  {material.video_links.length}
                </span>
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {material.video_links.map((link, idx) => (
                <a
                  key={idx}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', padding: '12px 14px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = C.goldBorder; (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(251,178,31,0.04)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = C.border; (e.currentTarget as HTMLAnchorElement).style.background = C.bg; }}
                >
                  <i className="pi pi-external-link" style={{ marginRight: '8px', color: C.gold }} />
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ── System Information ──────────────────────────────────────── */}
        <div style={{ background: C.bg, borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: C.sub, fontWeight: '600' }}>
            <i className="pi pi-hashtag" style={{ color: C.gold, fontSize: '0.8rem' }} />
            ID: <span style={{ fontFamily: 'monospace' }}>{material.id}</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: C.sub, fontWeight: '600' }}>
            <i className="pi pi-calendar" style={{ color: C.gold, fontSize: '0.8rem' }} />
            Created: {formatDateTime(material.created_at)}
          </span>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={onHide}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 28px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.2)', transition: 'all 0.2s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.85rem' }} />
            Close
          </button>
          {canManage && onEdit && <GlobalButton label="Edit" variant="primary" icon="pi pi-pencil" onClick={onEdit} />}
        </div>
      </div>
    </Dialog>
  );
};

export default PracticalMaterialDetailsModal;